/**
 * BookmarkPopup — names a bookmark, mirroring the light (FlashEvent) popup.
 *
 * Opens when a bookmark marker is clicked or one is placed (store.editing-
 * BookmarkBeat). A bookmark only has a name, so the popup is a single text field
 * (autofocused so you can type the name straight away) plus Delete. Draggable by
 * its header like the other popups; close with ×, Escape, or by clicking the
 * marker again.
 */

import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font } from '../ui/theme';
import { IcTrash, IcBookmark } from '../ui/icons';
import { useDraggablePanel } from '../ui/useDraggablePanel';

export default function BookmarkPopup() {
  const editingBookmarkBeat = useEditorStore(s => s.editingBookmarkBeat);
  const bookmarks = useEditorStore(s => s.bookmarks);
  const updateBookmarkName = useEditorStore(s => s.updateBookmarkName);
  const removeBookmark = useEditorStore(s => s.removeBookmark);
  const setEditingBookmarkBeat = useEditorStore(s => s.setEditingBookmarkBeat);
  const { panelRef, headerProps, draggedStyle } = useDraggablePanel();
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => setEditingBookmarkBeat(null);

  useEffect(() => {
    if (editingBookmarkBeat == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditingBookmarkBeat(null); };
    window.addEventListener('keydown', onKey);
    // Focus the name field so the user can type the label immediately.
    const id = requestAnimationFrame(() => { inputRef.current?.focus(); inputRef.current?.select(); });
    return () => { window.removeEventListener('keydown', onKey); cancelAnimationFrame(id); };
  }, [editingBookmarkBeat, setEditingBookmarkBeat]);

  if (editingBookmarkBeat == null) return null;
  const bm = bookmarks.find(b => Math.abs(b.beat - editingBookmarkBeat) < 0.01);
  if (!bm) return null;

  const del = () => { removeBookmark(bm.beat); close(); };

  return (
    <div ref={panelRef} style={{ ...panelStyle, ...draggedStyle }}>
      <div style={{ ...headerStyle, ...headerProps.style }} onPointerDown={headerProps.onPointerDown}>
        <span style={titleStyle}>
          <IcBookmark size={15} style={{ color: '#fff' }} />
          Bookmark · beat {bm.beat.toFixed(1)}
        </span>
        <button style={closeBtnStyle} onClick={close} title="Close (Esc)">×</button>
      </div>

      <input
        ref={inputRef}
        style={inputStyle}
        value={bm.name}
        placeholder="Name this bookmark…"
        onChange={e => updateBookmarkName(bm.beat, e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') close(); }}
      />

      <button style={deleteBtnStyle} onClick={del} data-hover-danger><IcTrash size={13} /> Delete bookmark</button>
    </div>
  );
}

// Matches the light popup's themed card.
const panelStyle: React.CSSProperties = {
  position: 'fixed', top: 76, left: '50%', transform: 'translateX(-50%)', zIndex: 60,
  width: 'min(280px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 90px)', overflowY: 'auto',
  padding: 14, borderRadius: 16,
  background: 'oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1.5px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
  backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
  display: 'flex', flexDirection: 'column', gap: 11, fontFamily: font.sans,
};
const headerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 };
const titleStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, color: color.ink, fontSize: 13, fontWeight: 700, letterSpacing: 0.2 };
const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '9px 13px', borderRadius: 999,
  background: 'oklch(0.14 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: color.ink, fontFamily: font.sans, fontSize: 13, outline: 'none',
};
const deleteBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '10px 10px', borderRadius: 999, cursor: 'pointer',
  background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.35)',
  color: '#F87171', fontSize: 12, fontWeight: 600, fontFamily: font.sans,
};
